import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Delete, Add, ArrowBack } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import SubscriptionPlanService from "../../../services/SubscriptionPlanService";
import { showNotification } from "../../../utils/notifications";

const TIERS = ["FREE", "BASIC", "PREMIUM", "ENTERPRISE"];
const PERIOD_UNITS = ["MONTH", "YEAR"];
const CURRENCIES = ["AED", "USD", "EUR"];

const periodLabel = (count, unit) =>
  `${count} ${unit.toLowerCase()}${count > 1 ? "s" : ""}`;

/** Create/edit a plan and (in edit mode) manage its prices and employee limit. */
const SubscriptionPlanForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [plan, setPlan] = useState({
    name: "",
    tier: "BASIC",
    description: "",
    displayOrder: 0,
    isPubliclyAvailable: true,
  });
  const [prices, setPrices] = useState([]);
  const [entLimit, setEntLimit] = useState("");
  const [entUnlimited, setEntUnlimited] = useState(false);
  const [newPrice, setNewPrice] = useState({ periodUnit: "MONTH", periodCount: 1, price: "", currency: "AED" });
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isEditing) loadPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadPlan = () => {
    setLoading(true);
    SubscriptionPlanService.getPlan(id)
      .then((data) => {
        setPlan({
          name: data.name || "",
          tier: data.tier || "BASIC",
          description: data.description || "",
          displayOrder: data.displayOrder || 0,
          isPubliclyAvailable: data.isPubliclyAvailable !== false,
        });
        setPrices(Array.isArray(data.prices) ? data.prices : []);
        const ent = (data.entitlements || []).find((e) => e.featureKey === "EMPLOYEE_COUNT");
        setEntUnlimited(ent ? !!ent.unlimited : false);
        setEntLimit(ent && ent.limitValue != null ? ent.limitValue : "");
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load plan");
        setLoading(false);
      });
  };

  const savePlan = () => {
    if (!plan.name || !plan.tier) {
      showNotification("Name and tier are required", "error");
      return;
    }
    setSaving(true);
    const req = isEditing
      ? SubscriptionPlanService.updatePlan(id, plan)
      : SubscriptionPlanService.createPlan(plan);
    req
      .then((data) => {
        showNotification("Plan saved", "success");
        setSaving(false);
        if (!isEditing && data && data.id) {
          navigate(`../edit/${data.id}`);
        }
      })
      .catch((e) => {
        setSaving(false);
        showNotification(e?.response?.data?.message || "Failed to save plan", "error");
      });
  };

  const addPrice = () => {
    if (!newPrice.price) {
      showNotification("Price is required", "error");
      return;
    }
    SubscriptionPlanService.addPrice(id, {
      ...newPrice,
      periodCount: Number(newPrice.periodCount),
      price: Number(newPrice.price),
    })
      .then(() => {
        showNotification("Duration added", "success");
        setNewPrice({ periodUnit: "MONTH", periodCount: 1, price: "", currency: "AED" });
        loadPlan();
      })
      .catch((e) => showNotification(e?.response?.data?.message || "Failed to add duration", "error"));
  };

  const removePrice = (priceId) => {
    SubscriptionPlanService.deactivatePrice(priceId)
      .then(() => {
        showNotification("Duration removed", "success");
        loadPlan();
      })
      .catch(() => showNotification("Failed to remove duration", "error"));
  };

  const saveEntitlement = () => {
    SubscriptionPlanService.setEntitlement(id, {
      featureKey: "EMPLOYEE_COUNT",
      unlimited: entUnlimited,
      limitValue: entUnlimited ? null : Number(entLimit),
    })
      .then(() => showNotification("Employee limit saved", "success"))
      .catch(() => showNotification("Failed to save limit", "error"));
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate("../")} sx={{ mb: 2 }}>
        Back to plans
      </Button>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {isEditing ? "Edit Plan" : "New Plan"}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={plan.name}
                onChange={(e) => setPlan({ ...plan, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Tier"
                value={plan.tier}
                onChange={(e) => setPlan({ ...plan, tier: e.target.value })}
              >
                {TIERS.map((t) => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={plan.description}
                onChange={(e) => setPlan({ ...plan, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Display Order"
                value={plan.displayOrder}
                onChange={(e) => setPlan({ ...plan, displayOrder: Number(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: "flex", alignItems: "center" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={plan.isPubliclyAvailable}
                    onChange={(e) => setPlan({ ...plan, isPubliclyAvailable: e.target.checked })}
                  />
                }
                label="Available in customer plan picker"
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={savePlan} disabled={saving}>
              {saving ? "Saving…" : "Save Plan"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {isEditing && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Employee Limit</Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={entUnlimited}
                        onChange={(e) => setEntUnlimited(e.target.checked)}
                      />
                    }
                    label="Unlimited"
                  />
                </Grid>
                <Grid item>
                  <TextField
                    type="number"
                    label="Max employees"
                    disabled={entUnlimited}
                    value={entLimit}
                    onChange={(e) => setEntLimit(e.target.value)}
                  />
                </Grid>
                <Grid item>
                  <Button variant="outlined" onClick={saveEntitlement}>Save Limit</Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Billing Durations</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Duration</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {prices.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align="center">No durations yet.</TableCell>
                      </TableRow>
                    )}
                    {prices.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell>{periodLabel(p.periodCount, p.periodUnit)}</TableCell>
                        <TableCell>{p.price} {p.currency}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" color="error" onClick={() => removePrice(p.id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Add duration</Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={6} sm={2}>
                  <TextField
                    select fullWidth label="Unit" value={newPrice.periodUnit}
                    onChange={(e) => setNewPrice({ ...newPrice, periodUnit: e.target.value })}
                  >
                    {PERIOD_UNITS.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField
                    type="number" fullWidth label="Count" value={newPrice.periodCount}
                    onChange={(e) => setNewPrice({ ...newPrice, periodCount: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    type="number" fullWidth label="Price" value={newPrice.price}
                    onChange={(e) => setNewPrice({ ...newPrice, price: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField
                    select fullWidth label="Currency" value={newPrice.currency}
                    onChange={(e) => setNewPrice({ ...newPrice, currency: e.target.value })}
                  >
                    {CURRENCIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Button fullWidth variant="contained" startIcon={<Add />} onClick={addPrice}>
                    Add
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default SubscriptionPlanForm;
